import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { useAuthStore } from './stores/auth';
import { Landing } from './pages/Landing';
import { Dashboard } from './pages/Dashboard';
import { KeywordAnalysis } from './pages/KeywordAnalysis';
import { BulkAnalysis } from './pages/BulkAnalysis';
import { GoldenKeyword } from './pages/GoldenKeyword';
import { Discover } from './pages/Discover';
import { Rank } from './pages/Rank';
import { Categories } from './pages/Categories';
import { BlogCoach } from './pages/BlogCoach';
import { SerpAnalysis } from './pages/SerpAnalysis';
import { BlogCompare } from './pages/BlogCompare';
import { Trending } from './pages/Trending';
import { Help } from './pages/Help';
import { MissingCheck } from './pages/MissingCheck';
import { PostQuality } from './pages/PostQuality';
import { Radar } from './pages/Radar';
import { BlogAnalysis } from './pages/BlogAnalysis';
import { Settings } from './pages/Settings';
import { Season } from './pages/Season';
import { Mod } from './pages/Mod';
import { Admin } from './pages/Admin';
import { NotFound } from './pages/NotFound';
import type { UserRole } from '@keywordgod/shared';

/** "/" 분기: 로그인 → Dashboard / 비로그인 → Landing */
function Home() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  if (loading) return null;
  return user ? <Dashboard /> : <Landing />;
}

/** 로그인 필요 페이지 가드 */
function Protected({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const ROLE_PRIORITY: Record<UserRole, number> = { member: 0, moderator: 1, admin: 2 };

/** 역할 기반 가드 (Mod, Admin 페이지용) */
function RoleGuard({
  min,
  children,
}: {
  min: UserRole;
  children: React.ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (ROLE_PRIORITY[user.role] < ROLE_PRIORITY[min]) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: <Home /> },
      { path: 'analyze', element: <Protected><KeywordAnalysis /></Protected> },
      { path: 'analyze/:keyword', element: <Protected><KeywordAnalysis /></Protected> },
      { path: 'bulk', element: <Protected><BulkAnalysis /></Protected> },
      { path: 'golden', element: <Protected><GoldenKeyword /></Protected> },
      { path: 'season', element: <Season /> },
      { path: 'landing', element: <Landing /> },
      { path: 'discover', element: <Protected><Discover /></Protected> },
      { path: 'rank', element: <Protected><Rank /></Protected> },
      { path: 'categories', element: <Categories /> },
      { path: 'blog-coach', element: <Protected><BlogCoach /></Protected> },
      { path: 'serp-analysis', element: <Protected><SerpAnalysis /></Protected> },
      { path: 'blog-compare', element: <Protected><BlogCompare /></Protected> },
      { path: 'trending', element: <Protected><Trending /></Protected> },
      { path: 'radar', element: <Protected><Radar /></Protected> },
      { path: 'blog-analysis', element: <Protected><BlogAnalysis /></Protected> },
      { path: 'missing', element: <Protected><MissingCheck /></Protected> },
      { path: 'post-quality', element: <Protected><PostQuality /></Protected> },
      { path: 'help', element: <Help /> },
      { path: 'settings', element: <Protected><Settings /></Protected> },
      { path: 'mod', element: <RoleGuard min="moderator"><Mod /></RoleGuard> },
      { path: 'admin', element: <Admin /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
